import { FolderTreeSelect } from '../../CommonComponents/FolderTreeSelect/FolderTreeSelect';
import { Map } from 'immutable';
import { COPY_PLAN_TO_FOLDER, COPY_PLAN_TO_PUBLICATION, FOLDER_SELECT_MENU_HEIGHT } from '@opengov/stories-shared/constants';
import { IStory } from '@opengov/stories-shared/types';
import { RouteComponentProps } from '@reach/router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';

import { IStoriesFolder, IStoryListMessages, IStoryListSelection } from '../../../../types';
import { navigateToFolderList, navigateToPublicationFolderList } from '../../../state/navigation';
import { findPublicationIdBySectionId, getFolderById } from '../../../utils';
import { useCopyStories } from '../hooks';
import { useUpdatePublicationUpdatedAt } from '../../Publications/PublicationView/hooks/usePublicationMutations';
import { getPublicationName } from '../../../common/utils';

import { styles } from './CopyStoriesModal.styles';

export interface ICopyStoriesModalProps extends RouteComponentProps {
  entityId: number;
  entitySubdomain: string;
  messages: IStoryListMessages;
  currentFolderId?: string;
  selectedStories: Map<string, IStory>;
  folderStructure: IStoriesFolder[];
  publicationFoldersTree: IStoriesFolder[];
  userId: number;
  isPublicationCopyStories?: boolean;
  sectionId?: string;
  handleUnselectAll(): void;
  onClose(): void;
  setSectionId?(section: IStoryListSelection | null): void;
}

export const CopyStoriesModal: React.FC<ICopyStoriesModalProps> = (props) => {
  const {
    entityId,
    entitySubdomain,
    currentFolderId,
    folderStructure,
    publicationFoldersTree,
    messages,
    selectedStories,
    isPublicationCopyStories = false,
    userId,
    sectionId,
    handleUnselectAll,
    onClose,
    setSectionId
  } = props;
  const updatePublicationUpdatedAtMutation = useUpdatePublicationUpdatedAt();
  const copyStoriesMutation = useCopyStories({
    title: messages.somethingWentWrongError,
    body: messages.copyStoriesFailed
  });
  const [copyPlan, setCopyPlan] = useState<string>(isPublicationCopyStories ? COPY_PLAN_TO_PUBLICATION : COPY_PLAN_TO_FOLDER);
  const [targetFolderId, setTargetFolderId] = useState(currentFolderId);
  const [currentPublicationName, setCurrentPublicationName] = useState('');

  // folderStructure + publicationFoldersTree flow in from the parent (SelectionToolbar/StoryList)
  // via useStoryFolders + usePublicationFoldersTree — no local fetch needed here.
  const isFetchingStoryFolders = false;
  const isFetchingPublicationsTree = false;

  const onConfirm = () => {
    // entitySubdomain is required by useCopyStories
    copyStoriesMutation.mutate(
      {
        ids: [...selectedStories].map(([id]) => id),
        folderId: targetFolderId,
        entityId,
        entitySubdomain
      },
      {
        onSuccess: () => {
          const targetFolder = getFolderById(folderStructure, targetFolderId) as IStoriesFolder;
          onClose();
          handleUnselectAll();
          if (copyPlan === COPY_PLAN_TO_PUBLICATION) {
            const pubIdBySectionId = findPublicationIdBySectionId(publicationFoldersTree, targetFolderId);
            updatePublicationUpdatedAtMutation.mutate({ publicationId: pubIdBySectionId, userId });
            if (setSectionId && targetFolderId === sectionId) {
              setSectionId(null);
            }
            navigateToPublicationFolderList(entitySubdomain, pubIdBySectionId, targetFolderId);
          } else {
            navigateToFolderList(entitySubdomain, targetFolderId);
            // Cache already invalidated by the hook — accordion will refetch automatically.
            // Navigate only; selection state driven by route.
            void targetFolder;
          }
        }
      }
    );
  };

  const handleCopyPlanChange = (_e: React.ChangeEvent<HTMLInputElement>, value: string): void => {
    setCopyPlan(value);
    if (
      (value === COPY_PLAN_TO_PUBLICATION && isPublicationCopyStories) ||
      (value === COPY_PLAN_TO_FOLDER && !isPublicationCopyStories)
    ) {
      setTargetFolderId(currentFolderId);
    } else {
      setTargetFolderId(null);
    }
  };

  const renderStoriesList = () => {
    const titles = [...selectedStories]
      .slice(0, 2)
      .map(([_id, story]) => story.title)
      .join(', ');
    const more = selectedStories.size - 2;
    return (
      <Box sx={styles.storiesListBlock} data-test="copying-stories-list">
        <Typography variant="body2">{messages.copying} </Typography>
        <Box sx={styles.storiesList}>
          <Typography variant="body2" component="strong" fontWeight="bold">{titles}</Typography>
        </Box>
        {more > 0 && <Typography variant="body2"> and {more} other</Typography>}
      </Box>
    );
  };

  useEffect(() => {
    const publicationName = getPublicationName(copyPlan, targetFolderId, publicationFoldersTree);
    setCurrentPublicationName(publicationName);
  }, [targetFolderId, copyPlan, publicationFoldersTree]);

  return (
    <div data-test="copy-stories-modal">
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="copy-stories-dialog-title">
        <DialogTitle id="copy-stories-dialog-title" sx={styles.dialogTitle}>
          {messages.copyStories}
          <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <Typography variant="body2" color="text.secondary">{messages.copyStoriesPickLocation}</Typography>

          {selectedStories.size > 0 && renderStoriesList()}

          <Box>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={styles.radioGroup}>{messages.copyTo}</FormLabel>
              <RadioGroup
                sx={styles.radioGroup}
                value={copyPlan}
                onChange={handleCopyPlanChange}
                data-test="copy-radio-group"
                row
              >
                <FormControlLabel
                  value={COPY_PLAN_TO_FOLDER}
                  sx={styles.radioButton}
                  control={
                    <Radio
                      inputProps={{ 'data-test': 'copy-plan-to-folder', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                  }
                  label={messages.copyToFolder}
                />
                <FormControlLabel
                  value={COPY_PLAN_TO_PUBLICATION}
                  sx={styles.radioButton}
                  control={
                    <Radio
                      inputProps={{ 'data-test': 'copy-plan-to-publication', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                  }
                  label={messages.copyToPublication}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {copyPlan === COPY_PLAN_TO_FOLDER && (
            <Box sx={styles.spacer}>
              <Typography variant="body2" sx={styles.formLabel}>{messages.copyTo}</Typography>
              <Box sx={styles.locationSelectContainer}>
                <FolderTreeSelect
                  activeNodeId={targetFolderId}
                  data={folderStructure}
                  dataTest="folder-accordion-select"
                  disabled={!folderStructure}
                  maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
                  onNodeSelect={(node) => setTargetFolderId(node.id)}
                  showFilter={true}
                  filterPlaceholder={messages.filterFolders}
                />
                {isFetchingStoryFolders && <CircularProgress size={20} />}
              </Box>
            </Box>
          )}

          {copyPlan === COPY_PLAN_TO_PUBLICATION && (
            <Box sx={styles.spacer}>
              <Typography variant="body2" sx={styles.formLabel}>{messages.publicationLocation}</Typography>
              <Box sx={styles.locationSelectContainer}>
                <FolderTreeSelect
                  activeNodeId={targetFolderId}
                  data={publicationFoldersTree}
                  dataTest="folder-accordion-select"
                  disabled={isFetchingPublicationsTree && !publicationFoldersTree.length}
                  maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
                  onNodeSelect={(node) => {
                    const isPublicationFolders = !!publicationFoldersTree.filter((folder) => folder.id === node.id).length;
                    return isPublicationFolders ? setTargetFolderId(null) : setTargetFolderId(node.id);
                  }}
                  showFilter={true}
                  filterPlaceholder="Filter sections by name"
                />
                {isFetchingPublicationsTree && <CircularProgress size={20} />}
                {currentPublicationName && (
                  <Typography variant="body2">{messages.publication}: {currentPublicationName}</Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={onClose}
            data-test="cancel-copy-stories-button"
          >
            {messages.cancel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onConfirm}
            data-test="confirm-copy-stories-button"
            disabled={!targetFolderId}
            startIcon={copyStoriesMutation.isLoading ? <CircularProgress size={16} color="inherit" aria-label="Loading" /> : undefined}
          >
            {messages.copy}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
